import { createContext, useContext } from 'react';
import type { useAgentHub } from '@/hooks/useAgentHub';
import type { FolderId } from '@/types';
import type { RowRefreshOutcome } from './rowRefresh';

export type HubApi = ReturnType<typeof useAgentHub>;

export interface ConsoleContextValue {
  /** The single shared /agenthub connection + its client→server methods. */
  hub: HubApi;
  /**
   * Re-fetch the SELECTED FOLDER's first page from the REST API (used after claim/close; kept as an alias for callers
   * that predate folders: the queue's claim, the spam page's undo). ConsoleShell's implementation is async and resolves
   * when the attempt ends, success or failure; read `sessionsError` to learn whether the rows were replaced. It reloads
   * ONE page of ONE folder, so a resolved reload is NOT proof that any particular row is fresh - re-read that row with
   * `refreshRow`.
   */
  reloadSessions: () => void | Promise<unknown>;
  /** The folder the console is listing (the URL owner pushes it in with `selectFolder`). */
  selectedFolder: FolderId;
  /** Select a folder; loads its first page when it has none yet or it is older than 30 s. */
  selectFolder: (folder: FolderId) => void;
  /** Load the next page of the selected folder (its own cursor). Ignored when there is no next page or one is loading. */
  loadMore: () => void | Promise<unknown>;
  /** True while the selected folder has a request running. */
  folderLoading: boolean;
  /** True when the selected folder's last request failed. */
  folderError: boolean;
  /**
   * Re-read ONE conversation from the server and apply the answer (200: replace + un-hide; 404: hide). The only way the
   * console re-checks a row after a push or a refusal. While it runs the row is "being re-read": a human-controlled
   * conversation's owner is not trusted. Never rejects; resolves with how it ended.
   */
  refreshRow: (sessionId: string) => Promise<RowRefreshOutcome>;
  /** Re-read the folder counts now (one request in flight at a time). */
  refreshCounts: () => void | Promise<unknown>;
  /** True while the initial session-list load is in flight. */
  sessionsLoading: boolean;
  /** True when the last session-list load failed (non-blocking — SignalR still feeds). */
  sessionsError: boolean;
}

export const ConsoleContext = createContext<ConsoleContextValue | null>(null);

/**
 * Access the shared hub + session-loading state from any view rendered under
 * <ConsoleShell/>. Throws if used outside the shell so a mis-mounted view is a
 * loud dev error, never a silent null-deref.
 */
export function useConsole(): ConsoleContextValue {
  const ctx = useContext(ConsoleContext);
  if (!ctx) {
    throw new Error('useConsole must be used within <ConsoleShell>');
  }
  return ctx;
}
